'use client';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import LazyLoadImage from '@repo/ui/src/components/LazyLoadImage';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import Button from '@repo/ui/src/components/Button';
import ProductPrice from '@repo/ui/src/components/Product/Price';
import { useVipAccess } from '@hooks/customer';
import buildLinkObj from '@repo/ui/src/functions/build-link';
import { logProduct } from '@utils/productWrapper';
import { Anchor } from '@repo/ui/src/components/Anchor';
import GrillCapacity from '../GrillCapacityAndMeasurement/_GrillCapacity';

interface Props {
  buttonLabel: string;
  buttonText: string;
  product: any;
  index: number;
}

const GrillFeatureComparisonItem = ({ buttonLabel, buttonText, product, index }: Props) => {
  const { locale } = useCurrentUrl();
  const { vip } = useVipAccess();

  if (!product?.data?.productCapacityInfo) return null;
  const extendedLabel = `${buttonLabel}: ${product.shortTitle}`;
  const closedImage = product?.data?.frontFacingImages?.closedImage;
  const closedImgUrl = closedImage?.image ? buildImageSrc(closedImage, 600, 1, 1) : null;

  return (
    <div
      className="flex flex-col justify-center items-stretch text-center flex-1 relative z-10 h-full"
      role="group"
      aria-label="product"
    >
      <a
        href={buildLinkObj({ link: product.deliveryKey })?.href}
        aria-label={product.title}
        className="relative -mb-[25%] pb-[100%] -z-10 transform duration-500 md:-mb-[30%] hover:md:-translate-y-3"
      >
        {closedImgUrl && (
          <LazyLoadImage
            imgComponent={
              <img
                src={closedImgUrl}
                alt={closedImage.image.name}
                className="image absolute w-[90%] h-[90%] sm:w-[70%] sm:h-[70%] left-1/2 translate-x-[-50%]"
              />
            }
          />
        )}
      </a>
      <h2 className="tra-text-web-heading-2 self-center w-full bg-neutral-700 text-warm-100 p-6 [&_span]:text-brand-300">
        {product.shortTitleMain ? (
          <>
            {product.shortTitleMain} <span>{product.shortTitlePrimary}</span>
          </>
        ) : (
          <>{product.title}</>
        )}
      </h2>
      <div className="bg-white flex-grow-1 py-10 px-6 md:py-11 md:px-10">
        <h4 className="tra-text-web-heading-4">
          {product.data.productCapacityInfo?.grillFeatureLabels?.capacity}
        </h4>
        <GrillCapacity {...product.data.productCapacityInfo} />
        <ProductPrice
          product={product}
          align="center"
          variant="h5"
          component="p"
          gutterBottom
          vip={vip}
        />
        <div className="flex justify-center">
          <Button
            link={product.deliveryKey}
            label={extendedLabel}
            as={Anchor}
            onClick={() => {
              logProduct('PRODUCT_CLICKED', {
                product: product,
                list_name: 'Grill Feature Comparison',
                locale,
                position: index,
              });
            }}
          >
            {buttonText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GrillFeatureComparisonItem;
